/* ==========================================================================
   lb-pdp-gallery.js — hybrid PDP gallery

   Does five things and nothing else:
     1. desktop: cross-fade between plates in one fixed frame — arrows and
        thumbnails both call the same setActive(); the page never scrolls
     2. mobile:  rAF-throttled index from the snap track
     3. shared:  counter, progress bar, thumbnail rail, polite announcement
     4. zoom + variant sync

   Explicitly NOT here, and that is the point:
     - no ResizeObserver (the old build ran one per media item, on document.body)
     - no IntersectionObserver. It existed to say which plate you had scrolled
       to in the vertical stack. There is no stack any more: on desktop the
       active image is whatever was clicked, so observing scroll would answer a
       question nobody asks.
     - no matchMedia branching for LAYOUT (CSS owns the breakpoint); matchMedia
       here only decides behaviour, never appearance.
     - no debounce on scroll (the old 100ms debounce is why the counter lagged
       the finger); rAF coalesces to one update per painted frame
     - no d-none swapping of any kind

   Mobile variant sync is intentionally absent: theme.js already does it
   correctly via .js-carousel-items + item.offsetLeft, and the markup is shaped
   so that call lands on the right snap position. Duplicating it would fight it.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTOR = 'sht-prd-slideshow.lb-gallery';
  /* MUST match the CSS breakpoint exactly. If these two ever disagree, JS
     thinks one model is live while CSS renders the other — e.g. arrows wired
     up on a swipe track, or jump() scrolling a frame that cannot scroll. */
  var DESKTOP = '(min-width: 1024px)';
  var REDUCED = '(prefers-reduced-motion: reduce)';

  function Gallery(root) {
    this.root = root;
    root.lbGallery = this;   // handle for debugging and for other theme scripts

    this.index = 0;
    this.frame = 0;
    this.announceTimer = 0;
    this.observer = null;

    this.mql = window.matchMedia(DESKTOP);

    // Bound once: they live on the root, which survives a re-render.
    this.bindKeys();
    this.watchRoot();

    this.refresh(false);
  }

  /* (Re)query every child and bind its listeners.

     component-add-to-cart.js re-renders this element on EVERY variant change
     (`slideshow.innerHTML = fetched.innerHTML` while data-hide-variant-image
     is "false", which the gallery requires). A size pick on the PDP is a
     variant change, so without this the arrows, rail and zoom were bound to
     detached nodes and the photo snapped back to plate 1 after the first pick. */
  Gallery.prototype.refresh = function (restore) {
    var root = this.root;
    var keep = this.index;

    this.track = root.querySelector('.lb-gallery__track');
    if (!this.track) return;

    this.plates = toArray(this.track.querySelectorAll('.lb-plate'));
    if (!this.plates.length) return;

    this.steps = toArray(root.querySelectorAll('[data-lb-jump]'));
    this.rail = root.querySelector('.lb-rail__list');
    this.bars = toArray(root.querySelectorAll('.lb-gallery__bar i'));
    this.counters = toArray(root.querySelectorAll('[data-lb-current]'));
    this.status = root.querySelector('[data-lb-status]');
    this.lastRevealed = null;

    this.bindTrackScroll();
    this.bindJumps();
    this.bindZoom();
    this.bindVariantSync();
    this.bindArrows();

    if (!restore) return;

    // Stay on the photograph the shopper was looking at (or the one the
    // theme's setActiveMedia() chose for the new variant just before the
    // re-render). index = -1 forces a full repaint of the new markup.
    var i = clamp(keep, 0, this.plates.length - 1);
    this.index = -1;
    this.setActive(i, false);
    if (!this.mql.matches) this.track.scrollLeft = this.plates[i].offsetLeft;
  };

  Gallery.prototype.watchRoot = function () {
    if (!('MutationObserver' in window)) return;
    var self = this;
    new MutationObserver(function () {
      if (!self.track || !self.track.isConnected) self.refresh(true);
    }).observe(this.root, { childList: true });
  };

  /* --- shared state ------------------------------------------------------ */

  Gallery.prototype.setActive = function (i, announce) {
    if (i === this.index) return;
    this.index = i;

    var label = pad(i + 1);

    for (var c = 0; c < this.counters.length; c++) {
      this.counters[c].textContent = label;
    }
    for (var b = 0; b < this.bars.length; b++) {
      this.bars[b].classList.toggle('is-on', b === i);
    }
    for (var s = 0; s < this.steps.length; s++) {
      if (s === i) this.steps[s].setAttribute('aria-current', 'true');
      else this.steps[s].removeAttribute('aria-current');
    }

    this.paint(i);
    this.revealThumb(i);

    if (announce !== false) this.announce(i);
  };

  /* Keep the active thumbnail inside the rail's own scrollport.

     Deliberately NOT scrollIntoView(): that scrolls every scrollable ancestor
     including the document, so selecting a thumbnail would yank the page. This
     moves the rail and nothing else, and only when the thumb is actually out of
     view — so a rail that already shows everything never moves at all. */
  Gallery.prototype.revealThumb = function (i) {
    var rail = this.rail;
    var thumb = this.steps[i] && this.steps[i].parentNode;   // the <li>
    if (!rail || !thumb) return;
    if (rail.scrollHeight <= rail.clientHeight) return;      // nothing to scroll

    var top = thumb.offsetTop;
    var bottom = top + thumb.offsetHeight;

    if (top < rail.scrollTop) rail.scrollTop = top;
    else if (bottom > rail.scrollTop + rail.clientHeight) {
      rail.scrollTop = bottom - rail.clientHeight;
    }
  };

  /* One sentence, only once movement has settled. Announcing on every frame
     would make the live region unusable. */
  Gallery.prototype.announce = function (i) {
    if (!this.status) return;
    var self = this;
    clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(function () {
      self.status.textContent = 'Image ' + (i + 1) + ' of ' + self.plates.length;
    }, 300);
  };

  /* --- 1 : desktop cross-fade ------------------------------------------- */

  /* Toggling a class is the whole transition — CSS owns the 220ms fade. Doing
     it in JS would duplicate the timing in two places and bypass
     prefers-reduced-motion, which the stylesheet already honours. */
  Gallery.prototype.paint = function (i) {
    for (var p = 0; p < this.plates.length; p++) {
      this.plates[p].classList.toggle('is-active', p === i);
    }
  };

  Gallery.prototype.bindArrows = function () {
    var self = this;
    var prev = this.root.querySelector('.js-lb-prev');
    var next = this.root.querySelector('.js-lb-next');
    var last = this.plates.length - 1;

    /* Wrap rather than clamp. With a visible counter, wrapping is unsurprising,
       and it means neither arrow is ever a dead disabled control. */
    if (prev) prev.addEventListener('click', function () {
      self.jump(self.index <= 0 ? last : self.index - 1);
    });
    if (next) next.addEventListener('click', function () {
      self.jump(self.index >= last ? 0 : self.index + 1);
    });
  };

  /* --- 2 : mobile track scroll ------------------------------------------- */

  Gallery.prototype.bindTrackScroll = function () {
    var self = this;
    this.track.addEventListener('scroll', function () {
      if (self.mql.matches) return;            // desktop: the observer owns this
      if (self.frame) return;
      self.frame = requestAnimationFrame(function () {
        self.frame = 0;
        self.setActive(self.readIndex());
      });
    }, { passive: true });
  };

  Gallery.prototype.readIndex = function () {
    var w = this.plates[0].getBoundingClientRect().width;
    if (!w) return 0;
    var i = Math.round(this.track.scrollLeft / w);
    return clamp(i, 0, this.plates.length - 1);
  };

  /* --- 4 : quick jump ---------------------------------------------------- */

  Gallery.prototype.bindJumps = function () {
    var self = this;

    for (var i = 0; i < this.steps.length; i++) {
      this.steps[i].addEventListener('click', function (e) {
        self.jump(Number(e.currentTarget.dataset.lbJump) || 0);
      });
    }
  };

  Gallery.prototype.bindKeys = function () {
    var self = this;

    // Up/Down (and Home/End) move through the index once it has focus.
    this.root.addEventListener('keydown', function (e) {
      var step = e.target.closest && e.target.closest('[data-lb-jump]');
      if (!step) return;

      var at = Number(step.dataset.lbJump) || 0;
      var to = null;

      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') to = at + 1;
      else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') to = at - 1;
      else if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = self.steps.length - 1;
      else return;

      e.preventDefault();
      to = clamp(to, 0, self.steps.length - 1);

      /* preventScroll: focus() scrolls the element into view by default, which
         nudged the page on every arrow key. The rail is sticky so the thumb is
         already on screen, and revealThumb() handles the rail's own scrollport
         — the browser's scroll is redundant and visible. */
      try { self.steps[to].focus({ preventScroll: true }); }
      catch (err) { self.steps[to].focus(); }      // older Safari ignores the options object

      self.jump(to);
    });
  };

  Gallery.prototype.jump = function (i) {
    var plate = this.plates[i];
    if (!plate) return;

    if (!this.mql.matches) {
      /* Mobile only. offsetLeft IS the snap position, because the track has no
         horizontal padding — the same fact theme.js's variant sync relies on. */
      var smooth = !window.matchMedia(REDUCED).matches;
      try {
        this.track.scrollTo({ left: plate.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      } catch (err) {
        this.track.scrollLeft = plate.offsetLeft;   // Safari < 15.4
      }
    }

    /* Desktop deliberately does nothing here. Changing a photograph must not
       move the page — that is the whole point of the fixed frame. The
       cross-fade in setActive() is the entire transition. */

    this.setActive(i);
  };

  /* --- 5a : zoom -> existing dialog -------------------------------------- */

  Gallery.prototype.bindZoom = function () {
    var buttons = this.root.querySelectorAll('.js-lb-zoom');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function (e) {
        var btn = e.currentTarget;
        var dialog = document.getElementById(btn.dataset.galleryDialogId);
        // sht-dialog.showModal(trigger, restoreFocus) — see assets/theme.js
        if (dialog && typeof dialog.showModal === 'function') dialog.showModal(btn, true);
      });
    }
  };

  /* --- 5b : desktop variant sync ----------------------------------------- */

  /* theme.js's setActiveMedia() marks the variant's plate by adding `d-block`
     on desktop. We do not control that code and it dispatches no event, so we
     observe the one signal it does emit. Mobile is left alone — the same
     function already scrolls the track there, correctly. */
  Gallery.prototype.bindVariantSync = function () {
    if (!('MutationObserver' in window)) return;
    var self = this;

    if (this.observer) this.observer.disconnect();
    this.observer = new MutationObserver(function (records) {
      if (!self.mql.matches) return;

      for (var i = 0; i < records.length; i++) {
        var el = records[i].target;
        if (el.classList &&
            el.classList.contains('d-block') &&
            el.classList.contains('lb-plate')) {
          if (el === self.lastRevealed) return;
          self.lastRevealed = el;
          self.setActive(Number(el.dataset.index) || 0);   // cross-fade, no scroll
          return;
        }
      }
    });
    this.observer.observe(this.track, { subtree: true, attributes: true, attributeFilter: ['class'] });
  };

  /* --- helpers ------------------------------------------------------------ */

  function toArray(n) { return Array.prototype.slice.call(n); }
  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function pad(n) { return n < 10 ? '0' + n : String(n); }

  /* --- boot --------------------------------------------------------------- */

  function init(scope) {
    var roots = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].dataset.lbReady) continue;
      roots[i].dataset.lbReady = '1';
      new Gallery(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
